import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CircledImage from '../../../../SolvPatternLibrary/CircleImage';
import StarRating from '../../../../SolvPatternLibrary/StarRating';
import { colors } from '../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { formatPhone, pluralize } from '../../../../../core/util/string';
import { getMilesFromMeters } from '../../../../../core/util/geo';
import { emptyFunction } from '../../../../../core/util/function';
import { getLocationRatingAndRatingCount, isBookable } from '../../../../../core/util/location';
import { createLocationUrlFromLocation } from '../../../../../core/url/location';
import { analyticsTrackEvent } from '../../../../../core/analytics';
import { CDP_TOUCH_NEARBY_CLINIC } from '../../../../../core/analytics/events';
import { getImageObject, PHOTO } from '../../../../SolvPatternLibrary/Picture/util';
import { formatLocationImageAlt } from '../../../../../core/util/seo';

const Card = styled.a`
  list-style: none;
  display: list-item;
  max-height: 150px;
  padding: 20px;
  background: ${colors.white};
  -webkit-font-smoothing: antialiased;
  cursor: pointer;
  transition: background 100ms ease;

  &:hover {
    background: ${colors.fatherJohnMisty};
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
`;

const InfoContainer = styled.div`
  margin: 0 0 0 20px;
  vertical-align: top;
`;

const CallToAction = styled.div`
  height: 32px;
  width: 115px;
  background-color: ${colors.crunchBerry};
  font-family: ${fontFamily.medium};
  font-size: ${fontSize.default};
  color: ${colors.white};
  text-align: center;
  line-height: 30px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  border-radius: 4px;
  padding: 0 20px;
`;

const ClinicName = styled.h3`
  margin: 0 0 6px;
  color: ${colors.link};
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.default};
  text-decoration: none;

  ${Card}:hover & {
    text-decoration: underline;
  }
`;

const ClinicInfo = styled.div`
  margin: 0 0 6px;
  color: ${colors.lanaDelGrey};
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
`;

const ClinicImage = styled(CircledImage)`
  flex-grow: 0;
  flex-shrink: 0;
  height: 60px;
  width: 60px;
`;

const ClinicStars = styled(StarRating)`
  display: inline-block;
`;

const ClinicRating = styled.div`
  padding-bottom: 6px;
`;

const RatingCount = styled.span`
  color: ${colors.lanaDelGrey};
  font-family: ${fontFamily.book};
  font-size: ${fontSize.small};
  line-height: 24px;
  vertical-align: top;
  padding: 0 0 0 5px;
`;

export default class NearbyCard extends PureComponent {
  static propTypes = {
    location: PropTypes.object.isRequired,
    className: PropTypes.string,
    onClick: PropTypes.func,
  };

  static defaultProps = { onClick: emptyFunction };

  constructor(props) {
    super(props);
    const { city, state, phone, distanceFromCurrentLocation } = props.location;
    const milesAway = getMilesFromMeters(distanceFromCurrentLocation);
    this.info = `${city}, ${state}${milesAway ? ` | ${milesAway} mi away` : ''}`;
    this.ctaText = isBookable(props.location) ? 'Book Online' : `Call ${formatPhone(phone)}`;
    const { rating, ratingCount } = getLocationRatingAndRatingCount(props.location);
    this.rating = rating || 0;
    this.ratingCount = ratingCount;
  }

  handleClick = e => {
    const {
      onClick,
      location: { id, uclId, isSolvPartner },
    } = this.props;
    analyticsTrackEvent(CDP_TOUCH_NEARBY_CLINIC, {
      uclId,
      locationId: id,
      partner: isSolvPartner,
    });
    onClick(e);
  };

  render = () => {
    const {
      className,
      location,
      location: { displayNamePrimary },
    } = this.props;
    const imageObject = getImageObject(location, {
      imageType: PHOTO,
      imageIndex: 0,
      showDefaultPictureIfMissing: true,
    });

    return (
      <Card
        className={className}
        onClick={this.handleClick}
        href={`${createLocationUrlFromLocation(location)}`}
      >
        <Flex>
          <ClinicImage imageObject={imageObject} alt={formatLocationImageAlt(location)} />
          <InfoContainer>
            <ClinicName>{displayNamePrimary}</ClinicName>
            <ClinicInfo>{this.info}</ClinicInfo>
            <ClinicRating>
              <ClinicStars
                size={fontSize.mediumSmall}
                rating={this.rating}
                identifier={displayNamePrimary}
              />
              <RatingCount>
                {this.ratingCount || 'No'} {pluralize(this.ratingCount, 'rating')}
              </RatingCount>
            </ClinicRating>
            <CallToAction
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                window.location = `${createLocationUrlFromLocation(location)}?ctcModal=true`;
              }}
            >
              {this.ctaText}
            </CallToAction>
          </InfoContainer>
        </Flex>
      </Card>
    );
  };
}
